import { BedOccupancyDataPointsCount } from 'src/types/individual-dashboard';
import { getReferenceLines, getTooltipConfig, trendLines } from './config';
import { ChartPayloadItem, PATIENT_STATUS } from './types';
import { TrendingOverviewItemData } from 'src/routes/IndividualDashboard/modules/dashboard/types';
import { isNullOrUndefined } from 'src/utils/parser';
import { REFERENCE_LINE_ORIENTATION } from '@neteera/neteera-web-nextgen';

export const getPatientStatus = (
  trendingItemData: TrendingOverviewItemData,
  bedOccupancy: BedOccupancyDataPointsCount,
): PATIENT_STATUS => {
  const { dataPointsCount, inBedDataPointsCount } = bedOccupancy;
  if (trendingItemData?.hr || trendingItemData?.rr) {
    return PATIENT_STATUS.IN_BED;
  }

  if (dataPointsCount === 0 && inBedDataPointsCount === 0) {
    return PATIENT_STATUS.DEVICE_DISCONNECTED;
  }

  if (
    Number.isFinite(dataPointsCount) &&
    dataPointsCount > 0 &&
    inBedDataPointsCount === 0
  ) {
    return PATIENT_STATUS.OUT_OF_BED;
  }

  return PATIENT_STATUS.NO_DATA;
};

const searchNestedPayloadDataValue = (
  nestedPayload: Record<string, string | number>,
  dataKey: string,
  naValueMessage: string,
): string | number => {
  const value = nestedPayload[dataKey];

  if (value || value === 0) {
    return value;
  }

  return naValueMessage;
};

export const hydrateTooltipPayload = (
  payload: ChartPayloadItem[] = [],
  tooltipConfig: ReturnType<typeof getTooltipConfig>,
  naValueMessage: string,
) => {
  const nestedPayload = payload?.[0]?.payload;

  return Object.keys(tooltipConfig).reduce(
    (acc: ChartPayloadItem[], key: string) => {
      if (payload.find(item => item.dataKey === key)) {
        return acc;
      }
      const newItem = {
        dataKey: key,
        value: nestedPayload
          ? searchNestedPayloadDataValue(nestedPayload, key, naValueMessage)
          : naValueMessage,
        payload: nestedPayload,
      };

      return [...acc, newItem] as ChartPayloadItem[];
    },
    payload,
  );
};

export const getVisibleTrendLines = (
  activeData: string | null,
  hiddenData: string[],
) => {
  if (activeData) {
    return trendLines?.map(line =>
      line.dataKey === activeData ? line : { ...line, hide: true },
    );
  }

  if (hiddenData.length > 0) {
    return trendLines?.map(line =>
      hiddenData.includes(line.dataKey) ? { ...line, hide: true } : line,
    );
  }

  return trendLines;
};
type NonNullItem = Extract<
  ReturnType<typeof getReferenceLines>,
  { value: number }
>;

type VisibleReferenceLines = (
  | {
      dataKey: string;
      value: string;
      yAxisId: string;
      orientation: REFERENCE_LINE_ORIENTATION;
    }
  | {
      dataKey: string;
      value: number;
      yAxisId: string;
      orientation: REFERENCE_LINE_ORIENTATION;
    }
)[];

export const getVisibleReferenceLines = (
  referenceLines: ReturnType<typeof getReferenceLines> = [],
  activeData: string | null,
  hiddenData: string[],
) => {
  if (activeData) {
    return referenceLines?.filter(
      (line): line is NonNullItem =>
        (line.dataKey === activeData ||
          line.dataKey === `${activeData}Baseline`) &&
        !isNullOrUndefined<number | null | string>(line.value),
    );
  }

  if (hiddenData.length > 0) {
    return referenceLines?.filter(
      (line): line is NonNullItem =>
        !hiddenData.includes(line.dataKey) &&
        !isNullOrUndefined<number | null | string>(line.value),
    );
  }

  return referenceLines as VisibleReferenceLines;
};
